import React, { useState, useMemo, useEffect } from 'react';
import CopyBtn from './CopyBtn';

/* Vue matrice size×TP + table décomposée pick-par-pick.
   Consomme EXACTEMENT le shape §2.4 (result_json, schema_version===2).
   Réutilise les classes CSS .tdp-* (panel) — l'hôte fournit le <style>. */

const trunc = (s) => (s ? `${s.slice(0, 6)}…${s.slice(-4)}` : '—');
const SOLSCAN = (x) => `https://solscan.io/account/${x}`;
const GMGN_TOKEN = (x) => `https://gmgn.ai/sol/token/${x}`;
const DEXSCREENER = (x) => `https://dexscreener.com/solana/${x}`;

// Le mint stocké est souvent un placeholder 'bc:<bc_pda>' (vrai mint jamais capturé, INV-010)
// → liens explorer cassés. Vrai mint = r.ui_mint (résolu côté backend via tokens.ui_mint).
const realMintOf = (r) => {
  if (r.ui_mint && !r.ui_mint.startsWith('bc:')) return r.ui_mint;
  if (r.mint && !r.mint.startsWith('bc:')) return r.mint;
  return null;
};
const bcPdaOf = (r) => r.bc_pda || (r.mint && r.mint.startsWith('bc:') ? r.mint.slice(3) : null);

const fmtNum = (v, d = 2) => (v == null ? <span className="tdp-muted">—</span> : v.toFixed(d));
const fmtPct = (v, d = 1) => (v == null ? '—' : `${v.toFixed(d)}%`);
const fmtSigned = (v, suffix = '', d = 2) => {
  if (v == null) return <span className="tdp-muted">—</span>;
  return <span className={v >= 0 ? 'tdp-pos' : 'tdp-neg'}>{v >= 0 ? '+' : ''}{v.toFixed(d)}{suffix}</span>;
};
// Date de création (first_seen_unix, secondes) injectée au read par _bt_enrich_real_mint.
const fmtUnixDate = (u) => (u == null ? <span className="tdp-muted">—</span>
  : new Date(u * 1000).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }));

const PAGE_SIZE = 25;

const COL_KEYS = {
  'Mint':       'mint',
  'Créé':       'first_seen_unix',
  'Entry slot': 'entry_slot',
  'Peak×':      'peak_x',
  'TP%':        'tp_target_pct',
  'TP touché':  'tp_touched',
  'Outcome':    'outcome',
  'NetRet%':    'net_ret_pct',
  'SOL PnL':    'sol_pnl',
  'Rug':        'rug',
};

const NUM_COLS = new Set(['entry_slot', 'peak_x', 'tp_target_pct', 'net_ret_pct', 'sol_pnl']);

export default function BacktestMatrixView({ result }) {
  // v2 : cells sous entities[0].cells ; v3 (cohorte multi-stratégie) : sous entities[0].strategies[0].cells.
  // Le shape d'une cellule (agg/rows/tp_pct/size_sol) est identique entre v2 et v3.
  const ent = result?.entities?.[0];
  const cells = ent?.cells || ent?.strategies?.[0]?.cells || [];
  const sizes = useMemo(() => [...new Set(cells.map(c => c.size_sol))], [cells]);
  const tps = useMemo(() => [...new Set(cells.map(c => c.tp_pct))], [cells]);

  const [size, setSize] = useState(cells[0]?.size_sol);
  const [tp, setTp] = useState(cells[0]?.tp_pct);
  const [sortKey, setSortKey] = useState('first_seen_unix');
  const [sortDir, setSortDir] = useState('desc');
  const [page, setPage] = useState(0);

  // Résolution de la cellule active (null si guards non passés)
  const cell = cells.find(c => c.size_sol === size && c.tp_pct === tp) || cells[0] || null;
  const rows = cell?.rows || [];

  const sortedRows = useMemo(() => {
    const arr = [...rows];
    const dir = sortDir === 'asc' ? 1 : -1;
    arr.sort((a, b) => {
      const va = a[sortKey], vb = b[sortKey];
      const na = va === null || va === undefined, nb = vb === null || vb === undefined;
      if (na && nb) return 0;
      if (na) return 1;
      if (nb) return -1;
      if (typeof va === 'string' || typeof vb === 'string') return dir * String(va).localeCompare(String(vb));
      if (typeof va === 'boolean' || typeof vb === 'boolean') return dir * ((va ? 1 : 0) - (vb ? 1 : 0));
      return dir * (va - vb);
    });
    return arr;
  }, [rows, sortKey, sortDir]);

  // Reset page quand cellule ou tri change
  useEffect(() => { setPage(0); }, [size, tp, sortKey, sortDir]);

  // Tableau comparatif : cellules triées par size_sol puis tp_pct asc
  const sortedCells = useMemo(() => {
    return [...cells].sort((a, b) => {
      if (a.size_sol !== b.size_sol) return a.size_sol - b.size_sol;
      return a.tp_pct - b.tp_pct;
    });
  }, [cells]);

  // Early returns APRES tous les hooks
  if (!result || (result.schema_version !== 2 && result.schema_version !== 3)) {
    return <div className="tdp-empty">Format de résultat obsolète (schema_version non supporté).</div>;
  }
  if (cells.length === 0) {
    return <div className="tdp-empty">Aucune cellule dans le résultat.</div>;
  }

  const a = cell?.agg || {};

  const totalPages = Math.max(1, Math.ceil(sortedRows.length / PAGE_SIZE));
  const pageRows = sortedRows.slice(page * PAGE_SIZE, page * PAGE_SIZE + PAGE_SIZE);

  const handleColClick = (key) => {
    if (key === sortKey) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortKey(key);
      setSortDir('desc');
    }
  };

  const thStyle = { cursor: 'pointer', userSelect: 'none' };

  return (
    <div>
      <div className="tdp-ctrl">
        <span className="lbl">Size SOL</span>
        {sizes.map(s => (
          <button key={s} className={`tdp-btn ${size === s ? 'on' : ''}`} onClick={() => setSize(s)}>{s}</button>
        ))}
      </div>
      <div className="tdp-ctrl">
        <span className="lbl">TP %</span>
        {tps.map(t => (
          <button key={t} className={`tdp-btn ${tp === t ? 'on' : ''}`} onClick={() => setTp(t)}>+{t}%</button>
        ))}
      </div>

      <div className="tdp-sub" style={{ margin: '8px 0 4px' }}>Comparatif par cellule</div>
      <table className="tdp-tbl">
        <thead><tr>
          {sizes.length > 1 && <th>Size</th>}
          <th>TP%</th>
          <th className="num">EV med</th>
          <th className="num">WR%</th>
          <th className="num">Peak× méd</th>
          <th className="num">Rug%</th>
          <th className="num">SOL PnL</th>
          <th className="num">N</th>
        </tr></thead>
        <tbody>
          {sortedCells.map((c) => {
            const ag = c.agg || {};
            const isActive = c.size_sol === size && c.tp_pct === tp;
            return (
              <tr
                key={`${c.size_sol}-${c.tp_pct}`}
                style={{ background: isActive ? 'var(--surface2)' : undefined, cursor: 'pointer' }}
                onClick={() => { setSize(c.size_sol); setTp(c.tp_pct); }}
              >
                {sizes.length > 1 && <td>{c.size_sol}</td>}
                <td>+{c.tp_pct}%</td>
                <td className="num">{fmtSigned(ag.ev_median_pct, '%')}</td>
                <td className="num">{fmtPct(ag.win_rate_pct)}</td>
                <td className="num">{ag.median_peak_x == null ? <span className="tdp-muted">—</span> : `${fmtNum(ag.median_peak_x)}×`}</td>
                <td className="num">{fmtPct(ag.rug_rate_pct)}</td>
                <td className="num">{fmtSigned(ag.sol_pnl_total, '', 4)}</td>
                <td className="num">{`${ag.n_valid ?? '—'}/${ag.n_picks ?? '—'}`}</td>
              </tr>
            );
          })}
        </tbody>
      </table>

      <div className="tdp-score">
        <div className="tdp-kpi"><div className="k">EV mean</div><div className="v">{fmtSigned(a.ev_mean_pct, '%')}</div></div>
        <div className="tdp-kpi"><div className="k">EV median</div><div className="v">{fmtSigned(a.ev_median_pct, '%')}</div></div>
        <div className="tdp-kpi"><div className="k">Win %</div><div className="v">{fmtPct(a.win_rate_pct)}</div></div>
        <div className="tdp-kpi"><div className="k">TP-fill %</div><div className="v">{fmtPct(a.tp_fill_rate_pct)}</div></div>
        <div className="tdp-kpi"><div className="k">Rug %</div><div className="v">{fmtPct(a.rug_rate_pct)}</div></div>
        <div className="tdp-kpi"><div className="k">Peak× médian</div><div className="v">{fmtNum(a.median_peak_x)}×</div></div>
        <div className="tdp-kpi"><div className="k">SOL PnL total</div><div className="v">{fmtSigned(a.sol_pnl_total, '', 4)}</div></div>
        <div className="tdp-kpi"><div className="k">n_valid / n_picks</div><div className="v">{a.n_valid ?? '—'} / {a.n_picks ?? '—'}</div></div>
      </div>

      {rows.length === 0 ? <div className="tdp-empty">Aucun pick valide pour cette cellule.</div> : (
        <>
          <table className="tdp-tbl">
            <thead><tr>
              {Object.entries(COL_KEYS).map(([label, key]) => (
                <th
                  key={key}
                  className={NUM_COLS.has(key) ? 'num' : undefined}
                  style={thStyle}
                  onClick={() => handleColClick(key)}
                >
                  {label}{sortKey === key ? (sortDir === 'asc' ? ' ▲' : ' ▼') : ''}
                </th>
              ))}
            </tr></thead>
            <tbody>
              {pageRows.map((r, i) => (
                <tr key={r.bc_pda || r.mint || i}>
                  <td>
                    {(() => {
                      const rm = realMintOf(r);
                      const pda = bcPdaOf(r);
                      const label = rm || pda || r.mint || '';
                      const solTarget = rm || pda;  // Solscan /account/ marche pour mint ET bc_pda
                      return (
                        <>
                          <span className="tdp-mint" title={label} onClick={() => navigator.clipboard?.writeText(label)}>{trunc(label)}</span><CopyBtn text={label} />{' '}
                          {solTarget && <a className="tdp-tl sol" href={SOLSCAN(solTarget)} target="_blank" rel="noopener noreferrer">SS</a>}
                          {rm && <a className="tdp-tl gm" href={GMGN_TOKEN(rm)} target="_blank" rel="noopener noreferrer">GM</a>}
                          {rm && <a className="tdp-tl dx" href={DEXSCREENER(rm)} target="_blank" rel="noopener noreferrer">DEX</a>}
                        </>
                      );
                    })()}
                  </td>
                  <td style={{ fontSize: 10 }}>{fmtUnixDate(r.first_seen_unix)}</td>
                  <td className="num">{r.entry_slot ?? <span className="tdp-muted">—</span>}</td>
                  <td className="num">{fmtNum(r.peak_x)}×</td>
                  <td className="num">{r.tp_target_pct == null ? '—' : `+${r.tp_target_pct}%`}</td>
                  <td>{r.tp_touched ? <span className="tdp-pos">oui</span> : <span className="tdp-muted">non</span>}</td>
                  <td>{r.outcome || <span className="tdp-muted">—</span>}</td>
                  <td className="num">{fmtSigned(r.net_ret_pct, '%')}</td>
                  <td className="num">{fmtSigned(r.sol_pnl, '', 4)}</td>
                  <td>{r.rug ? <span className="tdp-badge rug">rug</span> : <span className="tdp-muted">—</span>}</td>
                </tr>
              ))}
            </tbody>
          </table>

          {totalPages > 1 && (
            <div className="tdp-ctrl" style={{ marginTop: 8, gap: 8 }}>
              <button className="tdp-btn" onClick={() => setPage(p => p - 1)} disabled={page === 0}>‹ Préc</button>
              <span className="tdp-sub">Page {page + 1}/{totalPages} ({sortedRows.length} lignes)</span>
              <button className="tdp-btn" onClick={() => setPage(p => p + 1)} disabled={page >= totalPages - 1}>Suiv ›</button>
            </div>
          )}
        </>
      )}

      {cell?.rows_truncated && (
        <div className="tdp-sub" style={{ marginTop: 8 }}>Premières lignes affichées (tri net_ret desc, cellule tronquée).</div>
      )}
      <div className="tdp-sub" style={{ marginTop: 8, color: 'var(--text2,#8a8f98)' }}>
        Backtest contrefactuel slot-1 sur les tokens de l'entité choisie — biais de sélection résiduel non éliminé.
      </div>
    </div>
  );
}
